import { addYears } from 'date-fns'
import { useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { uploadPhoto } from '~/features/Registration/helpers'
import { FixButtonWrapper, FormWrapper } from '~/features/Registration/styled'
import { AggregatorOccupation } from '~/shared/api'
import {
  countriesOptions,
  taxSystemEnumOptions,
} from '~/shared/config/constants'
import { AggregatorOccupationCodeEnum } from '~/shared/config/enums'
import { formatDateForApi } from '~/shared/lib/date'
import { useTaxiparkQueryParams } from '~/shared/lib/useTaxiparkQueryParams'
import { getObject } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/Buttons'
import {
  FormProvider,
  DatePicker,
  TextInput,
  Select,
  PickPhotos,
  AsyncSelect,
} from '~/shared/ui/form'
import { FormValues } from './model'

type FormProps = {
  onSuccess: (formValues: FormValues) => void
  form: UseFormReturn<FormValues>
  isLoading: boolean
}

export function Form({ onSuccess, form, isLoading }: FormProps) {
  const { taxiparkIdQuery } = useTaxiparkQueryParams()

  const { watch, resetField, setValue } = form

  const aggregatorOccupationOption = watch('aggregatorOccupationOption')
  const aggregatorOccupationCode = getObject(aggregatorOccupationOption)?.code

  const isIndividualEntrepreneurRus =
    aggregatorOccupationCode ===
    AggregatorOccupationCodeEnum.individual_entrepreneur_rus

  useEffect(() => {
    setValue('isIndividualEntrepreneur', isIndividualEntrepreneurRus)
  }, [isIndividualEntrepreneurRus, setValue])

  useEffect(() => {
    if (!isIndividualEntrepreneurRus) {
      resetField('taxSystem')
      resetField('inn')
      resetField('email')
    }
  }, [isIndividualEntrepreneurRus, resetField])

  return (
    <FormProvider form={form} onSuccess={onSuccess}>
      <FormWrapper>
        <AsyncSelect
          name='aggregatorOccupationOption'
          label='Тип занятости'
          modalTitle='Тип занятости'
          fetchOptions={AggregatorOccupation.fetchOptions}
        />

        {isIndividualEntrepreneurRus && (
          <>
            <Select
              options={taxSystemEnumOptions}
              name='taxSystem'
              label='Система налогообложения ип'
              modalTitle='Система налогообложения ип'
            />

            <TextInput name='inn' label='ИНН' />

            <TextInput name='email' label='Email' />
          </>
        )}

        <Select
          options={countriesOptions}
          name='drivingLicenseIssuingCountry'
          label='Страна выдачи'
          modalTitle='Страна выдачи'
          canSearch
        />
        <TextInput name='drivingLicenseNumber' label='Номер ВУ' />
        <DatePicker name='drivingLicenseIssueDate' label='Дата выдачи ВУ' />
        <DatePicker
          name='drivingExperienceStartDate'
          label='Дата начала водительского стажа'
        />
        <DatePicker
          name='drivingLicenseExpirationDate'
          label='Дата окончания ВУ'
          ionDatetimeProps={{ max: formatDateForApi(addYears(new Date(), 20)) }}
        />
        <PickPhotos
          descriptions='Добавьте две фотографии водительского удостоверения: с лицевой и с обратной стороны'
          name='drivingLicensePhotos'
          multiple
          fnUpload={(file: Blob) => uploadPhoto(file, taxiparkIdQuery)}
          maxPhotos={2}
        />

        <FixButtonWrapper>
          <Button
            loading={isLoading}
            variant='limeFlooded'
            size='large'
            type='submit'
            fullWidth
          >
            Продолжить
          </Button>
        </FixButtonWrapper>
      </FormWrapper>
    </FormProvider>
  )
}
